/**
 * What the curate screen offers as one figure.
 *
 * The API returns one entry per compound, but the positive controls are read as
 * a set: Ketoconazole answers for co-incubation and Azamulin for the two
 * pre-incubation conditions, and the question a scientist asks is whether the
 * run's controls behaved, not whether one of them did. So they are composed into
 * a single "standards" view that comes first, exactly as the exported STD figure
 * draws them. Test compounds follow, and the vehicle last, since it is a check
 * rather than a result.
 */

import { CONDITION_ORDER } from "./conditions";
import { sig } from "./format";

export const STANDARDS_KEY = "__standards__";
export const FIGURE_TITLE_PREFIX = "CYP3A4 Midazolam";

function byCondition(a, b) {
  return CONDITION_ORDER.indexOf(a.condition) - CONDITION_ORDER.indexOf(b.condition);
}

/** One flat series per condition, carrying the compound it came from. */
function seriesOf(detail) {
  return detail.conditions.map((c) => ({
    key: `${detail.block.compound}|${c.condition}`,
    condition: c.condition,
    compound: detail.block.compound,
    fit: c.fit,
    observations: c.observations,
  }));
}

function compoundView(detail) {
  const { block } = detail;
  return {
    key: block.compound,
    name: block.compound,
    figureTitle: `${FIGURE_TITLE_PREFIX} ${block.compound}`,
    kind: block.is_vehicle ? "vehicle — not fitted" : `${block.series.length} series`,
    note: block.is_vehicle
      ? "Vehicle control. Normalised and plotted so the solvent can be seen not to inhibit, but never IC50-fitted."
      : `Starting concentration ${block.starting_concentrations
          .map((v) => sig(v))
          .join(" and ")} µM, six-point twofold dilution, series ${block.series.join(", ")}.`,
    showCompound: false,
    series: [...seriesOf(detail)].sort(byCondition),
  };
}

function standardsView(controls) {
  const series = controls.flatMap(seriesOf).sort(byCondition);

  // "Ketoconazole for co-incubation, Azamulin for both pre-incubation conditions"
  // is the sentence a reviewer wants, and it has to be derived rather than
  // written down: which control covers which condition comes from the Substanzen
  // sheet and can differ between runs.
  const byCompound = new Map();
  series.forEach((s) => {
    byCompound.set(s.compound, [...(byCompound.get(s.compound) || []), s.condition]);
  });
  const note = [...byCompound.entries()]
    .map(([compound, conditions]) => `${compound} for ${conditions.join(" and ")}`)
    .join("; ");

  return {
    key: STANDARDS_KEY,
    name: "Positive controls",
    figureTitle: `${FIGURE_TITLE_PREFIX} STD`,
    kind: `${byCompound.size} controls`,
    note: `${note}. Each is a single determination per concentration, so these curves confirm the run behaved rather than measure a new compound.`,
    showCompound: true,
    series,
  };
}

/** Standards first, then the test compounds, then the vehicle. */
export function buildViews(compounds) {
  const controls = compounds.filter((c) => c.block.is_positive_control);
  const tests = compounds.filter((c) => !c.block.is_positive_control && !c.block.is_vehicle);
  const vehicles = compounds.filter((c) => c.block.is_vehicle);

  return [
    ...(controls.length ? [standardsView(controls)] : []),
    ...tests.map(compoundView),
    ...vehicles.map(compoundView),
  ];
}

/** Conditions present in a view, in the order the figures draw them. */
export function conditionsOf(view) {
  const seen = view.series.map((s) => s.condition);
  return CONDITION_ORDER.filter((c) => seen.includes(c));
}

export function outlierCount(view) {
  return view.series.reduce((total, s) => total + s.observations.filter((o) => !o.included).length, 0);
}
